import { useState } from 'react';
import { toast } from 'sonner';
import { useActionsTresorerie, useArretesCaisse, useCaisse } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { BoutonPdf, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { OngletsTresorerie, TableDossiers, aujourdhui, estCaissier, estResponsableTresorerie } from '@/components/tresorerie';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr, montant } from '@/lib/utils';

const COUPURES = ['100', '50', '20', '10', '5', '1'];

function DialogueArrete({ soldeTheorique, ouvert, onOpenChange }: { soldeTheorique: number; ouvert: boolean; onOpenChange: (o: boolean) => void }) {
  const actions = useActionsTresorerie();
  const [billetage, setBilletage] = useState<Record<string, string>>({});
  const [commentaire, setCommentaire] = useState('');
  const total = COUPURES.reduce((s, c) => s + Number(c) * (Number(billetage[c]) || 0), 0);
  const ecart = Math.round((total - soldeTheorique) * 100) / 100;
  const arreter = async () => {
    try {
      const a = await actions.arreterCaisse.mutateAsync({ date: aujourdhui(), billetage: Object.fromEntries(COUPURES.map((c) => [c, Number(billetage[c]) || 0])), commentaire: commentaire.trim() || undefined });
      toast.success(a.ecart === 0 ? 'Arrêté établi sans écart.' : `Arrêté établi avec un écart de ${montant(a.ecart, true)} : le responsable et le DF sont alertés.`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Arrêté impossible.');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Arrêté de caisse du {dateFr(aujourdhui())}</DialogTitle>
          <DialogDescription>Comptez les espèces par coupure ; le solde physique est comparé au solde théorique de {montant(soldeTheorique, true)}. Tout écart est notifié et commenté.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {COUPURES.map((c) => (
            <div key={c} className="space-y-1">
              <Label htmlFor={`c${c}`}>{c} USD</Label>
              <Input id={`c${c}`} type="number" min={0} step={1} value={billetage[c] ?? ''} onChange={(e) => setBilletage({ ...billetage, [c]: e.target.value })} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
          <span>Solde physique compté</span>
          <span className="num font-semibold">{montant(total, true)}</span>
        </div>
        <div className={cn('flex items-center justify-between px-3 text-sm', ecart !== 0 ? 'text-red-700' : 'text-emerald-700')}>
          <span>Écart avec le solde théorique</span>
          <span className="num font-semibold">{montant(ecart, true)}</span>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="commentaireArrete">Commentaire {ecart !== 0 ? '(obligatoire en cas d’écart)' : '(facultatif)'}</Label>
          <Textarea id="commentaireArrete" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={arreter} disabled={actions.arreterCaisse.isPending || (ecart !== 0 && commentaire.trim().length < 5)}>
            Établir l'arrêté
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Caisse (CA-1, CA-3, § 5.3) : solde, paiements en espèces, arrêté journalier avec billetage, contresignature. */
export function CaisseTresorerie() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const { data, isLoading, error } = useCaisse();
  const arretes = useArretesCaisse();
  const actions = useActionsTresorerie();
  const [dialogue, setDialogue] = useState(false);
  const contresigner = async (id: string) => {
    try {
      await actions.contresigner.mutateAsync(id);
      toast.success('Arrêté contresigné.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Contresignature impossible.');
    }
  };
  const peutArreter = estCaissier(profils) || estResponsableTresorerie(profils);

  return (
    <div>
      <PageHeader titre="Caisse" sousTitre={data?.caisse ? `${data.caisse.banque} · plafond par paiement ${data.caisse.plafondOperation === null ? 'paramètre général' : montant(data.caisse.plafondOperation)} · solde maximum ${data.caisse.soldeMaximum === null ? 'paramètre général' : montant(data.caisse.soldeMaximum)}` : undefined} actions={peutArreter && data && !data.arreteDuJour && <Button onClick={() => setDialogue(true)}>Arrêté de caisse du jour</Button>} />
      <OngletsTresorerie />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : !data.caisse ? (
        <Vide message="Aucune caisse paramétrée : créez un compte de type « Caisse » dans les comptes du Fonds." />
      ) : (
        <div className="space-y-6">
          {data.alertes.map((a, i) => (
            <Alert key={i} variant="destructive">
              <AlertDescription>{a}</AlertDescription>
            </Alert>
          ))}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Solde disponible" valeur={montant(data.caisse.soldeDisponible, true)} detail={`dernier arrêté ${data.caisse.dateReleve ? `du ${dateFr(data.caisse.dateReleve)}` : 'jamais'} : ${montant(data.caisse.soldeReleve, true)}`} accent={data.caisse.soldeDisponible < 0 ? 'critique' : undefined} />
            <Kpi label="Payés depuis l'arrêté" valeur={montant(data.caisse.payesNonAuReleve, true)} />
            <Kpi label="Programmés cette semaine" valeur={montant(data.caisse.programmesSemaine, true)} detail={`prévisionnel ${montant(data.caisse.soldePrevisionnel, true)}`} />
            <Kpi label="Arrêté du jour" valeur={data.arreteDuJour ? montant(data.arreteDuJour.soldePhysique, true) : 'à établir'} detail={data.arreteDuJour ? `écart ${montant(data.arreteDuJour.ecart, true)}${data.arreteDuJour.contresigneParNom ? ` · contresigné par ${data.arreteDuJour.contresigneParNom}` : ' · à contresigner'}` : 'en fin de journée'} accent={data.arreteDuJour ? (data.arreteDuJour.ecart === 0 ? 'ok' : 'critique') : undefined} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Paiements à préparer ({data.paiements.aPreparer.length})</CardTitle>
              <CardDescription>Transmis par le responsable de la trésorerie pour paiement en espèces.</CardDescription>
            </CardHeader>
            <CardContent>
              <TableDossiers dossiers={data.paiements.aPreparer} vide="Aucun paiement transmis." colonnes={['statut', 'semaine', 'delai']} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>En préparation ({data.paiements.enPreparation.length})</CardTitle>
              <CardDescription>Bon de caisse, pièce d'identité du bénéficiaire, reçu signé joint au FED.</CardDescription>
            </CardHeader>
            <CardContent>
              <TableDossiers dossiers={data.paiements.enPreparation} vide="Aucun paiement en préparation." colonnes={['statut', 'delai']} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Payés récemment</CardTitle>
            </CardHeader>
            <CardContent>
              <TableDossiers dossiers={data.paiements.payesRecents} vide="Aucun paiement en espèces récent." colonnes={['statut', 'paiement']} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Arrêtés de caisse</CardTitle>
              <CardDescription>Billetage, solde théorique contre solde physique, écart, signature du caissier et contresignature du responsable.</CardDescription>
            </CardHeader>
            <CardContent>
              {!arretes.data?.length ? (
                <Vide message="Aucun arrêté." />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Théorique</TableHead>
                      <TableHead className="text-right">Physique</TableHead>
                      <TableHead className="text-right">Écart</TableHead>
                      <TableHead className="hidden md:table-cell">Billetage</TableHead>
                      <TableHead>Signatures</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {arretes.data.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          {dateFr(a.date)}
                          {a.commentaire && <div className="max-w-[28ch] truncate text-xs text-muted-foreground">{a.commentaire}</div>}
                        </TableCell>
                        <TableCell className="num text-right">{montant(a.soldeTheorique, true)}</TableCell>
                        <TableCell className="num text-right">{montant(a.soldePhysique, true)}</TableCell>
                        <TableCell className={cn('num text-right font-semibold', a.ecart !== 0 && 'text-red-700')}>{montant(a.ecart, true)}</TableCell>
                        <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
                          {Object.entries(a.billetage)
                            .filter(([, n]) => n > 0)
                            .sort((x, y) => Number(y[0]) - Number(x[0]))
                            .map(([c, n]) => `${n} × ${c}`)
                            .join(' · ') || '—'}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>{a.caissierNom}</div>
                          {a.contresigneParNom ? (
                            <div className="text-muted-foreground">
                              contresigné par {a.contresigneParNom} · {dateHeureFr(a.dateContresignature)}
                            </div>
                          ) : estResponsableTresorerie(profils) && a.caissierId !== session?.utilisateur.agentId ? (
                            <Button size="sm" variant="outline" className="mt-1" onClick={() => contresigner(a.id)} disabled={actions.contresigner.isPending}>
                              Contresigner
                            </Button>
                          ) : (
                            <Badge variant="outline">à contresigner</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <BoutonPdf chemin={`/pdf/tresorerie/arretes/${a.id}`} nom={`arrete-caisse-${a.date}.pdf`} taille="icon" variant="ghost" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          {dialogue && <DialogueArrete soldeTheorique={data.caisse.soldeDisponible} ouvert onOpenChange={(o) => !o && setDialogue(false)} />}
        </div>
      )}
    </div>
  );
}
